import React from "react";

import { Node as ProseNode } from "prosemirror-model";

import { EditorConfig, ProseReact } from "~/lib/prose";
import { LinkMark } from "~/lib/prose/marks";
import { Mention, TextNode, Unstyled } from "~/lib/prose/nodes";
import { GlobalPlaceholder, InputRules, MentionProvider, Paste, TypeAhead } from "~/lib/prose/plugins";
import EmojiProvider, { PasteBuiltInEmoji } from "~/lib/prose/plugins/emoji";
import type { ObjectiveAttributes } from "~/okrs/Objective";

import styles from "~/okrs/components/CommentEditor.module.css";

function CommentsEditor({
	doc,
	placeholder = "",
	configRef,
	editorRef,
	contributors,
}: {
	doc: ProseNode | { [key: string]: unknown };
	onChange: (v: ProseNode) => void;
	placeholder?: string;
	configRef?: React.MutableRefObject<EditorConfig | undefined>;
	editorRef: React.MutableRefObject<ProseReact | null>;
	contributors: ObjectiveAttributes["contributors"];
}) {
	const editorContext = React.useMemo(() => {
		return {
			participants: contributors,
		};
	}, [contributors]);
	const editorConfig = React.useMemo(() => {
		const config = new EditorConfig({
			nodes: { doc: { content: "block+" } },
			extensions: [
				new TextNode(),
				new Unstyled({ disableIndentInputRule: true }),
				new Paste(),
				new GlobalPlaceholder({ text: placeholder }),
				new InputRules(),
				new Mention(),
				new TypeAhead({
					mountToDocument: true,
					providers: [new MentionProvider(), new EmojiProvider()],
				}),
				new LinkMark({ allowEditing: false }),
				new PasteBuiltInEmoji(),
			],
		});
		if (configRef) {
			configRef.current = config;
		}
		return config;
	}, [placeholder, configRef]);

	React.useEffect(() => {
		editorRef.current?.focusToEnd();
	});

	const controlledDoc = doc instanceof ProseNode ? doc : editorConfig.schema.nodeFromJSON(doc);
	return (
		<ProseReact
			ref={editorRef}
			config={editorConfig}
			controlledDoc={controlledDoc}
			context={editorContext}
			className={styles.newCommentInput}
			resetSelectionOnControlledDocChange={false}
		/>
	);
}

export default CommentsEditor;
